import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TableColType } from '@shared/enums/table.enum';
import { IAPIErrorObject } from '@shared/interfaces/api.interface';
import { ICandidate } from '@shared/interfaces/candidate.interface';
import { IProfile } from '@shared/interfaces/profile.interface';
import { IReference } from '@shared/interfaces/reference.interface';
import { IReminder } from '@shared/interfaces/reminder.interface';
import { ITableCol } from '@shared/interfaces/table.col.interface';
import { IUser } from '@shared/interfaces/user.interface';
import { Candidate } from '@shared/models/candidate.model';
import { Pagination } from '@shared/models/pagination.model';
import { CandidatesService } from '@shared/services/candidates.service';
import { LoggerService } from '@shared/services/logger.service';
import { PaginationReferencesService } from '@shared/services/pagination/pagination.references.service';
import { ReferencesService } from '@shared/services/references.service';
import { ToastService } from '@shared/services/toast.service';
import moment from 'moment';

import { SessionStorage } from 'ngx-webstorage';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-references-show',
  templateUrl: './references.show.component.html'
})
export class ReferencesShowComponent implements OnInit, OnDestroy {
  @SessionStorage() private user: IUser;

  public title = String('REFERENCES.SHOW.TITLE');
  public description = String('REFERENCES.SHOW.DESCRIPTION');
  public iconClass = String('lnr-contacts');
  public counter = Number(0);

  public page: Pagination<IReference>;
  public candidate: ICandidate;
  public referenceContacts: any[] = [];

  public profile: IProfile;
  public id: string;

  public openReferenceModal: Subject<{
    candidate: Partial<ICandidate>;
    reference: IReference;
  }> = new Subject();

  public openReferenceContactModal: Subject<{
    candidate: ICandidate;
  }> = new Subject();

  public rows: IReference[] = [];
  public cols: Partial<ITableCol>[] = [
    {
      headerText: 'TABLES.HEADERS.NAME',
      type: TableColType.Text,
      cellClassName: 'v-text--highlight',
      selector: 'full_name'
    },
    {
      headerText: 'TABLES.HEADERS.EMAIL',
      type: TableColType.Icon,
      selector: 'email_validity'
    },
    {
      headerText: 'TABLES.HEADERS.PHONE_NUMBER',
      type: TableColType.Icon,
      selector: 'phone_number_validity'
    },
    {
      headerText: 'TABLES.HEADERS.REQUESTED_AT',
      type: TableColType.Date,
      selector: 'created_at'
    },
    {
      headerText: 'TABLES.HEADERS.COMPLETED_AT',
      type: TableColType.Date,
      selector: 'filled_at'
    }
  ];

  private readonly constructorName: string = String(this.constructor.name);

  private paginationSubscription: Subscription;
  private routesSubscription: Subscription;

  constructor(
    private readonly _ar: ActivatedRoute,
    private readonly _candidates: CandidatesService,
    private readonly _logger: LoggerService,
    private readonly _paginationReferences: PaginationReferencesService,
    private readonly _references: ReferencesService,
    private readonly _toast: ToastService
  ) {
    this.page = new Pagination({});

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.afterSubmit = this.afterSubmit.bind(this);

    this.routesSubscription = this._ar.params.subscribe((res: Params) => {
      this.id = res.id;
      this.profile = this.user.profile;

      this._paginationReferences.setFilter({
        key: 'candidate',
        value: this.id
      });

      this.getCandidate();
      this.getCandidateReferences();
    });
  }

  public get pagination(): PaginationReferencesService {
    return this._paginationReferences;
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.paginationSubscription?.unsubscribe();
    this.routesSubscription?.unsubscribe();
  }

  public afterSubmit() {
    this.reloadPage();
  }

  public openModal(r?: IReference) {
    if (!!r) {
      this.openReferenceModal.next({
        candidate: new Candidate({
          id: this.id
        }),
        reference: r
      });
    } else {
      this.openReferenceContactModal.next({
        candidate: new Candidate({
          id: this.id
        })
      });
    }
  }

  public onButtonClick(e: Event, row: IReference) {
    e.stopPropagation();

    const url = `GET /reminders/references/${row.id}`;
    this._references.sendReferenceReminder(row.id).subscribe(
      (res: Partial<IReminder>) => {
        this._logger.info(this.constructorName, url, res);
        this._toast.success('Reminder sent');

        row.reminder = {
          ...row.reminder,
          latest_sent_at: moment().format(),
          rate_exceeded: true
        };

        this.reloadPage();
      },
      (err: IAPIErrorObject[]) => {
        this._logger.error(this.constructorName, url, err);

        this._toast.error(err[0].detail);
      }
    );
  }

  public onRowClick(r: IReference) {
    if (!r.filled) {
      this.openModal(r);
    }
  }

  public reloadPage() {
    this.getCandidate();

    this._paginationReferences.reloadPage(
      this._paginationReferences.currentPage
    );

    this._toast.success('References reloaded');
  }

  private getCandidate() {
    const url = `GET /candidates/${this.id}`;
    this._candidates.find(this.id).subscribe(
      (res: ICandidate) => {
        this._logger.info(this.constructorName, url, res);

        this.candidate = res;
        this.referenceContacts = res.reference_contacts;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  private getCandidateReferences() {
    Promise.resolve().then(() => {
      this.paginationSubscription = this._paginationReferences.page.subscribe(
        (page: Pagination<IReference>) => {
          this._logger.info(
            this.constructorName,
            `GET /references pagination`,
            page
          );

          this.page = page;
          this.rows = page.entries;
        }
      );

      this._paginationReferences.entries.subscribe((totalEntries: number) => {
        this.counter = totalEntries;
      });

      this._paginationReferences.fullReload();
    });
  }
}
